/**
 * ExploreFeatures component
 *
 * - Provides a UI for exploring product features, including charts and data tables.
 * - Accepts productName, tenantId, and productFamily as props.
 * - Uses custom hooks to fetch feature data and prepare chart data.
 * - Manages state for showing the data table and chart height.
 * - Handles loading, error, and empty states with dedicated components.
 * - Special handling for "Microsoft Intune":
 *   - Renders a chart for each feature and disables the data table toggle.
 *   - Flattens all feature data for controls.
 * - For other products:
 *   - Renders a single chart and data table for the selected features.
 * - Uses a ButtonWithPanel to open the feature explorer panel and trigger data fetch.
 * - All UI is wrapped in a Fluent UI Stack for layout.
 */
import { Stack } from "@fluentui/react";
import { FormEvent, MouseEvent, useCallback, useMemo, useState } from "react";
import { useFeatureData } from "./Hooks/useFeatureData.hook";
import { ExploreFeaturesPanelProps } from "./ExploreFeatures.interfaces";
import { useChartData } from "./Hooks/useChartData.hook";
import { FeatureDataTable } from "./Components/FeaturesDataTable";
import { FeatureChart } from "./Components/FeatureChart";
import { FeatureControls } from "./Components/FeatureControls";
import { EmptyState, ErrorState, LoadingState, NoFeatureSelectedState } from "./Components/FeatureStateMessages";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import ButtonWithPanel from "../ButtonWithPanel";
import { DEFAULT_CHART_HEIGHT } from "../../components/CustomerView/CustomerView.constants";



const ExploreFeaturesPanel = ({ productName, productFamily, customProps, idProps, data, isLoading, error }: ExploreFeaturesPanelProps) => {
  const [showDataTable, setShowDataTable] = useState(true);
  const [chartHeight, setChartHeight] = useState(DEFAULT_CHART_HEIGHT);

  const { rawFeaturesData, selectedFeatures, setSelectedFeatures, isLoading: featureDataLoading, error: featureDataError, triggerFetch } = useFeatureData({ customProps, idProps, productFamily, productName, data, isLoading, error });

  const selectedFeaturesChartData = useChartData(rawFeaturesData, selectedFeatures, customProps);

  const isIntune = productName === "Microsoft Intune";
  const hasNoData = rawFeaturesData?.length === 0;

  // Memoize event payloads to avoid unnecessary object creation
  const telemetryPayload = useMemo(
    () => ({
      productName,
      tenantId: idProps.tenantId,
      partnerOneId: idProps.partnerOneId,
      mpnId: idProps.mpnId,
      productFamily
    }),
    [productName, idProps.tenantId, idProps.partnerOneId, idProps.mpnId, productFamily]
  );

  const handleChartHeightChange = useCallback(
    (_event: FormEvent<HTMLDivElement>, option?: { key: number }) => {
      if (option) {
        setChartHeight(option.key);
        logEvent(customProps.tabName, customProps.pageName, "ExploreFeatures_" + customProps.apiName, {
          ...telemetryPayload,
          eventName: "ChartHeightChanged",
          chartHeight: option.key
        });
      }
    },
    [telemetryPayload]
  );

  const handleDataTableToggle = useCallback(
    (_event?: MouseEvent<HTMLElement>, checked?: boolean) => {
      setShowDataTable(!!checked);
      logEvent(customProps.tabName, customProps.pageName, "ExploreFeatures_" + customProps.apiName, {
        ...telemetryPayload,
        eventName: "DataTableToggle",
        showDataTable: !!checked
      });
    },
    [telemetryPayload]
  );

  const handlePanelOpen = useCallback(() => {
    triggerFetch();
    logEvent(customProps.tabName, customProps.pageName, "ExploreFeatures_" + customProps.apiName, {
      ...telemetryPayload,
      eventName: "PanelOpened"
    });
  }, [triggerFetch, telemetryPayload]);

  // Extracted content rendering logic for clarity and performance
  const renderIntuneContent = useCallback(
    () => (
      <>
        <FeatureControls
          chartHeight={chartHeight}
          disableDataTableToggle
          onChartHeightChange={handleChartHeightChange}
          onDataTableToggle={() => {}}
          rawFeatureData={rawFeaturesData.flatMap((feature) => feature.data || [])}
          selectedFeatures={selectedFeatures}
          setSelectedFeatures={setSelectedFeatures}
          showDataTable={false}
          customProps={customProps}
        />
        {selectedFeaturesChartData?.length === 0 && <NoFeatureSelectedState />}
        {selectedFeaturesChartData.map((chartData, idx) => (
          <FeatureChart
            key={`feature-chart-${idx}`}
            chartTitle={rawFeaturesData[idx]?.name}
            chartData={chartData}
            selectedFeatures={selectedFeatures}
            chartHeight={chartHeight}
            showDataTable={showDataTable}
          />
        ))}
      </>
    ),
    [
      chartHeight,
      handleChartHeightChange,
      rawFeaturesData,
      selectedFeatures,
      setSelectedFeatures,
      selectedFeaturesChartData,
      showDataTable
    ]
  );

  const renderDefaultContent = useCallback(
    () => (
      <>
        <FeatureControls
          rawFeatureData={rawFeaturesData[0]?.data || []}
          selectedFeatures={selectedFeatures}
          setSelectedFeatures={setSelectedFeatures}
          chartHeight={chartHeight}
          onChartHeightChange={handleChartHeightChange}
          customProps={customProps}
          showDataTable={showDataTable}
          onDataTableToggle={handleDataTableToggle}
        />
        <FeatureChart
          chartData={selectedFeaturesChartData[0] ?? {}}
          selectedFeatures={selectedFeatures}
          chartHeight={chartHeight}
          showDataTable={showDataTable}
        />
        <FeatureDataTable
          rawFeatureData={rawFeaturesData[0]?.data || []}
          selectedFeatures={selectedFeatures}
          showDataTable={showDataTable}
        />
      </>
    ),
    [
      chartHeight,
      handleChartHeightChange,
      handleDataTableToggle,
      rawFeaturesData,
      selectedFeatures,
      setSelectedFeatures,
      selectedFeaturesChartData,
      showDataTable
    ]
  );

  const renderContent = useCallback(() => {
    if (featureDataLoading) return <LoadingState />;
    if (featureDataError) {
      logException("ExploreFeatures", customProps.tabName, "useFeatureData", featureDataError);
      return <ErrorState />;
    }
    if (hasNoData) {
      logEvent(customProps.tabName, customProps.pageName, "ExploreFeatures_" + customProps.apiName, {
        ...telemetryPayload,
        eventName: "EmptyState"
      });
      return <EmptyState productName={productName} />;
    }
    return isIntune ? renderIntuneContent() : renderDefaultContent();
  }, [featureDataLoading, featureDataError, hasNoData, isIntune, renderIntuneContent, renderDefaultContent, telemetryPayload, productName]);

  return (
    <ButtonWithPanel
      buttonProps={{
        buttonContent: "Explore Features"
      }}
      panelProps={{
        panelTitle: `Feature Explorer — ${productName}`,
        isLoading: false
      }}
      onPanelOpen={handlePanelOpen}
    >
      <Stack tokens={{ childrenGap: 15 }} style={{ padding: 20 }}>
        {renderContent()}
      </Stack>
    </ButtonWithPanel>
  );
};

export default ExploreFeaturesPanel;
